import { Button } from "@msuite/ui/button";
import { FormSwitch, NumberInput, TextInput } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { cn } from "@msuite/ui/utils";
import type { VehicleFormSchema } from "@msuite/utils/schemas";
import { capitalize } from "@msuite/utils/strings";
import type { Enums } from "@msuite/utils/types";
import React, { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

/** Props Interface */
interface VehicleFormCraneProps {
	position: Enums<"cranePosition">;
}

export function VehicleFormCrane({ position }: VehicleFormCraneProps) {
	/** Context */
	const methods = useFormContext<VehicleFormSchema>();
	const rootKey = `crane${capitalize(position)}` as "craneFront" | "craneTail";

	/** Form */
	const [exists, _position, maximumLoad] = useWatch({
		control: methods.control,
		name: [`${rootKey}.exists`, `${rootKey}.position`, `${rootKey}.maximumLoad`],
	});

	/** Hooks */
	const { t } = useTranslation();

	/** Effects */
	useEffect(() => {
		if (!methods.getValues(rootKey)?.maximumLoad) {
			methods.setValue(`${rootKey}.maximumLoad`, [{ meter: 0, weightKg: 0, adcKg: 0 }]);
		}
		if (!methods.getValues(rootKey)?.position) {
			methods.setValue(`${rootKey}.position`, position);
		}
	}, [exists]);

	/** Functions */
	function handleOnAppendMaximumLoad(index: number) {
		const currentMaximumLoad = methods.getValues(`${rootKey}.maximumLoad`);
		const newMaximumLoad = [...(currentMaximumLoad || [])];
		newMaximumLoad.splice(index + 1, 0, { meter: 0, weightKg: 0, adcKg: 0 });
		methods.setValue(`${rootKey}.maximumLoad`, newMaximumLoad);
	}

	function handleOnDeleteMaximumLoad(index: number) {
		const currentMaximumLoad = methods.getValues(`${rootKey}.maximumLoad`);
		const newMaximumLoad = [...(currentMaximumLoad || [])];
		newMaximumLoad.splice(index, 1);
		methods.setValue(`${rootKey}.maximumLoad`, newMaximumLoad);
	}

	/** Render */
	return (
		<div className="v-stack gap-4">
			<FormSwitch
				control={methods.control}
				name={`${rootKey}.exists`}
				width="full"
			>
				<p>{t(`vehicles.${position}_crane`)}</p>
			</FormSwitch>
			{exists && (
				<React.Fragment>
					<TextInput
						label={t("general.name")}
						control={methods.control}
						name={`${rootKey}.name`}
						isRequired
						placeholder="z.B. HIAB X-HIPRO 302 E-B"
					/>
					<NumberInput
						label={t("vehicles.angle")}
						isRequired
						control={methods.control}
						name={`${rootKey}.angle`}
						variant="integer"
						className="w-[200px]"
						placeholder="0"
						min={0}
						max={359}
					/>
					<div className="v-stack gap-4">
						<p className="label">{t("vehicles.crane_load")}</p>
						<div className="h-stack card p-0 overflow-x-auto gap-0">
							{maximumLoad?.map((_, index) => (
								<React.Fragment key={index}>
									{index === 0 && (
										<div
											className={cn(
												"w-max whitespace-nowrap self-stretch flex flex-col border-r-[0.5px] border-color bg-primary-50 dark:bg-primary-950 flex-center px-1",
												"cursor-pointer",
											)}
											onClick={() => handleOnAppendMaximumLoad(index)}
										>
											<Icon
												size={6}
												name="Plus"
											/>
										</div>
									)}
									<div
										className="grid grid-rows-3 w-full min-w-[100px] relative"
										key={index}
									>
										<div className="absolute right-1 bottom-1">
											<Button
												icon={<Icon name="Trash2" />}
												size="xxs"
												variant="danger-ghost"
												onClick={() => {
													handleOnDeleteMaximumLoad(index);
												}}
											/>
										</div>
										<input
											className="input focus:ring-0 rounded-none border-0 shadow-none border-b-[0.5px]"
											type="number"
											{...methods.register(`${rootKey}.maximumLoad.${index}.adcKg`, {
												valueAsNumber: true,
											})}
											step={0.1}
										/>
										<input
											className="input focus:ring-0 rounded-none border-0 shadow-none border-b-[0.5px]"
											type="number"
											{...methods.register(`${rootKey}.maximumLoad.${index}.weightKg`, {
												valueAsNumber: true,
											})}
											step={0.1}
										/>
										<input
											className="input focus:ring-0 rounded-none border-0 shadow-none"
											type="number"
											{...methods.register(`${rootKey}.maximumLoad.${index}.meter`, {
												valueAsNumber: true,
											})}
											step={0.1}
										/>
									</div>
									<div
										className={cn(
											"w-max whitespace-nowrap self-stretch flex flex-col border-color bg-primary-50 dark:bg-primary-950 flex-center px-1",
											"cursor-pointer",
											index !== maximumLoad.length - 1 ? "border-x-[0.5px]" : "border-l-[0.5px]",
										)}
										onClick={() => handleOnAppendMaximumLoad(index)}
									>
										<Icon
											size={6}
											name="Plus"
										/>
									</div>
								</React.Fragment>
							))}
							<div className="w-max whitespace-nowrap self-stretch flex flex-col border-l-[0.5px] border-color bg-primary-50 dark:bg-primary-950 sticky right-0">
								<div className="whitespace-nowrap flex-1 border-b-[0.5px] border-color px-2 flex-center">
									<p>{t("vehicles.adc_kg")}</p>
								</div>
								<div className="whitespace-nowrap flex-1 border-b-[0.5px] border-color px-2 flex-center">
									<p>{t("vehicles.weight_kg")}</p>
								</div>
								<div className="whitespace-nowrap flex-1 border-b-[0.5px] border-transparent px-2 flex-center">
									<p>{t("vehicles.meter")}</p>
								</div>
							</div>
						</div>
					</div>
				</React.Fragment>
			)}
		</div>
	);
}
